import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TenantListItem } from '@/api/tenants/tenantsApi.types';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { useTenantsQuery } from '@/hooks/useTenantsQuery';

const RECENT_WORKSPACES_LIMIT = 6;

export function RecentWorkspacesPanel() {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const tenantsQuery = useTenantsQuery(1, '');
  const tenants =
    tenantsQuery.data === undefined ? [] : tenantsQuery.data.items.slice(0, RECENT_WORKSPACES_LIMIT);

  return (
    <Card as="section">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-9 h-9 rounded-xl bg-zoovpalelavender text-zooviolet flex items-center justify-center shrink-0">
          <Icon name="Building" />
        </span>
        <h2 className="text-sm font-bold text-zooindigo">{t('dashboard.recentWorkspaces.title')}</h2>
      </div>
      {tenantsQuery.isLoading === true ? (
        <div className="py-6 flex justify-center">
          <LoadingSpinner className="w-6 h-6" />
        </div>
      ) : tenantsQuery.isError === true ? (
        <QueryErrorState
          onRetry={function retry() {
            tenantsQuery.refetch();
          }}
        />
      ) : tenants.length === 0 ? (
        <p className="text-[13px] text-zoomuted py-1">{t('dashboard.recentWorkspaces.empty')}</p>
      ) : (
        <div className="divide-y divide-zooborder">
          {tenants.map(function renderWorkspace(tenant) {
            return (
              <WorkspaceRow
                key={tenant.id}
                tenant={tenant}
                onOpen={function open() {
                  navigate('/admin/practitioners/' + tenant.id);
                }}
              />
            );
          })}
        </div>
      )}
    </Card>
  );
}

interface WorkspaceRowProps {
  tenant: TenantListItem;
  onOpen: () => void;
}

function WorkspaceRow({ tenant, onOpen }: WorkspaceRowProps) {
  const { t } = useTranslation('admin');
  const ownerLabel =
    tenant.ownerName === null || tenant.ownerName.trim().length === 0
      ? t('dashboard.recentWorkspaces.noOwner')
      : tenant.ownerName;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full flex items-center justify-between gap-3 py-3 text-left"
    >
      <div className="min-w-0">
        <div className="text-[13px] font-bold text-zooindigo truncate">{tenant.name}</div>
        <div className="text-[11px] text-zoomuted truncate">{ownerLabel}</div>
      </div>
      {tenant.status === 'active' ? (
        <Badge tone="success" size="sm" className="shrink-0">
          {t('practitioners.statusActive')}
        </Badge>
      ) : (
        <Badge tone="muted" size="sm" className="shrink-0">
          {t('practitioners.statusSuspended')}
        </Badge>
      )}
    </button>
  );
}
